import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import type { PendingPrerequisite } from '../../../lib/prerequisites'

interface PrerequisiteWarningProps {
  exerciseTitle: string
  prerequisites: PendingPrerequisite[]
  onGoToPrerequisite: (exerciseId: number) => void
  onContinueAnyway: () => void
  onClose: () => void
}

export const PrerequisiteWarning: React.FC<PrerequisiteWarningProps> = ({
  exerciseTitle,
  prerequisites,
  onGoToPrerequisite,
  onContinueAnyway,
  onClose
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const first = prerequisites[0]
  const count = prerequisites.length
  const plural = count === 1 ? 'exercise' : 'exercises'

  // Portalled to the body: the page wrapper carries a transform, which would
  // otherwise make `fixed` resolve against it instead of the viewport.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="prerequisite-warning-title"
        className="relative w-full max-w-[340px] mx-4 bg-[#faf8f3] border border-[#e0d4be] rounded-xl shadow-xl p-5"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#b4831f] flex-shrink-0" strokeWidth={2.5} />
          <h2
            id="prerequisite-warning-title"
            className="text-sm font-semibold text-[#5c4a1f] tracking-tight"
          >
            {count} incomplete {plural} before this
          </h2>
        </div>

        <p className="mt-2 text-xs text-[#7a6a4a] leading-relaxed">
          <span className="font-medium text-[#5c4a1f]">{exerciseTitle}</span> may depend on
          {count === 1 ? ' ' : ' earlier work you haven’t finished, starting with '}
          <span className="font-medium text-[#5c4a1f]">{first?.title}</span>
          {count === 1 ? ', which you haven’t finished yet.' : '.'}
        </p>

        <div className="mt-4 flex gap-2">
          <button
            onClick={onContinueAnyway}
            className="flex-1 py-2 px-3 rounded-lg border border-[#e0d4be] text-xs font-medium text-[#7a6a4a] hover:bg-[#f0e8d8] transition-colors"
          >
            Continue anyway
          </button>
          {first && (
            <button
              onClick={() => onGoToPrerequisite(first.id)}
              className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold text-white transition-colors hover:opacity-90"
              style={{ background: '#7a6020' }}
              title={first.title}
            >
              Go there
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
